import axios from 'axios';

interface IframeAuthConfig {
  tokenParamName?: string;
  storageKey?: string;
  autoSetHeaders?: boolean;
}

class IframeAuthManager {
  private config: IframeAuthConfig = {
    tokenParamName: 'jtToken', // Use existing jtToken parameter
    storageKey: 'iframe_jwt_token',
    autoSetHeaders: true
  };

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  private getTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get(this.config.tokenParamName!);
    
    if (token) {
      // Store token in session storage for subsequent requests
      sessionStorage.setItem(this.config.storageKey!, token);
      
      // Remove token from URL to avoid exposing it
      // urlParams.delete(this.config.tokenParamName!);
      // const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}${window.location.hash}`;
      // window.history.replaceState({}, document.title, newUrl);
    }
    
    return token;
  }

  private getStoredToken(): string | null {
    return sessionStorage.getItem(this.config.storageKey!);
  }

  public initialize(config?: IframeAuthConfig): void {
    this.config = { ...this.config, ...config };
    
    if (this.isInIframe()) {
      // Try to get token from URL first
      let token = this.getTokenFromUrl();
      
      // If no token in URL, check session storage
      if (!token) {
        token = this.getStoredToken();
      }
      
      if (token && this.config.autoSetHeaders) {
        this.setAuthHeaders(token);
      }
      
      // Listen for postMessage authentication
      this.setupPostMessageListener();
    }
  }

  private setupPostMessageListener(): void {
    window.addEventListener('message', (event) => {
      // Validate origin if needed
      // if (event.origin !== 'https://trusted-parent.com') return;
      
      if (event.data && event.data.type === 'auth_token') {
        const token = event.data.token;
        if (token) {
          sessionStorage.setItem(this.config.storageKey!, token);
          if (this.config.autoSetHeaders) {
            this.setAuthHeaders(token);
          }
          
          // Notify parent that token was received
          if (event.source) {
            (event.source as Window).postMessage({ 
              type: 'auth_token_received', 
              success: true 
            }, event.origin);
          }
        }
      }
    });
  }

  public setAuthHeaders(token: string): void {
    // Set default Authorization header for axios
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Also set custom header for iframe detection
    axios.defaults.headers.common['X-Iframe-Token'] = token;
  }

  public getToken(): string | null {
    return this.getStoredToken();
  }

  public clearToken(): void {
    sessionStorage.removeItem(this.config.storageKey!);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Iframe-Token'];
  }

  public requestTokenFromParent(): void {
    if (this.isInIframe() && window.parent) {
      window.parent.postMessage({ type: 'request_auth_token' }, '*');
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      // Basic JWT expiration check
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp) {
        return Date.now() < payload.exp * 1000;
      }
      
      return true;
    } catch {
      return false;
    }
  }
}

// Create singleton instance
const iframeAuth = new IframeAuthManager();

// Helper hook for React components
export const useIframeAuth = () => {
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  return {
    isInIframe: isInIframe(),
    token: iframeAuth.getToken(),
    isAuthenticated: iframeAuth.isAuthenticated(),
    requestToken: () => iframeAuth.requestTokenFromParent(),
    clearToken: () => iframeAuth.clearToken()
  };
};

export default iframeAuth;